import { supabase } from "@/integrations/supabase/client";

// Device type detection utility
export const detectDeviceType = (): string => {
  const userAgent = navigator.userAgent;
  
  if (/tablet|ipad|playbook|silk/i.test(userAgent)) {
    return 'tablet';
  }
  if (/mobile|iphone|ipod|android|blackberry|opera|mini|windows\sce|palm|smartphone|iemobile/i.test(userAgent)) {
    return 'mobile';
  }
  return 'desktop';
};

// Analytics tracking service
export const analyticsService = {
  // Track when a user views an event
  async trackEventView(eventId: string, userId?: string, guestId?: string) {
    try {
      await supabase.from('event_analytics').insert({
        event_id: eventId,
        user_id: userId || null,
        guest_id: guestId || null,
        action_type: 'view',
        device_type: detectDeviceType(),
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error('Error tracking event view:', error);
    }
  },

  // Track when a user votes
  async trackVote(
    eventId: string,
    userId?: string,
    guestId?: string,
    opts?: { durationMs?: number }
  ) {
    try {
      await supabase.from('event_analytics').insert({
        event_id: eventId,
        user_id: userId || null,
        guest_id: guestId || null,
        action_type: 'vote',
        device_type: detectDeviceType(),
        user_agent: navigator.userAgent,
        duration_ms: opts?.durationMs ?? null,
      });
    } catch (error) {
      console.error('Error tracking vote:', error);
    }
  },

  // Track when a user comments
  async trackComment(eventId: string, userId?: string, guestId?: string) {
    try {
      await supabase.from('event_analytics').insert({
        event_id: eventId,
        user_id: userId || null,
        guest_id: guestId || null,
        action_type: 'comment',
        device_type: detectDeviceType(),
        user_agent: navigator.userAgent,
      });
    } catch (error) {
      console.error('Error tracking comment:', error);
    }
  },

  // Get analytics data for an event
  async getEventAnalytics(eventId: string) {
    try {
      const { data, error } = await supabase
        .from('event_analytics')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error getting event analytics:', error);
      return [];
    }
  },

  // Get device type distribution for an event
  async getDeviceTypeDistribution(eventId: string) {
    try {
      const { data, error } = await supabase
        .from('event_analytics')
        .select('device_type')
        .eq('event_id', eventId)
        .not('device_type', 'is', null);

      if (error) throw error;
      
      const deviceCounts = data.reduce((acc: Record<string, number>, item) => {
        const deviceType = item.device_type || 'unknown';
        acc[deviceType] = (acc[deviceType] || 0) + 1;
        return acc;
      }, {});

      const total = Object.values(deviceCounts).reduce((sum, count) => sum + count, 0);
      
      return Object.entries(deviceCounts).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value: Math.round((value / total) * 100)
      }));
    } catch (error) {
      console.error('Error getting device type distribution:', error);
      return [];
    }
  },

  // Get action counts by day for an event
  async getActionsByDay(eventId: string) {
    try {
      const { data, error } = await supabase
        .from('event_analytics')
        .select('action_type, created_at')
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const actionsByDay = data.reduce((acc: Record<string, Record<string, number>>, item) => {
        const date = new Date(item.created_at).toISOString().split('T')[0];
        if (!acc[date]) acc[date] = {};
        acc[date][item.action_type] = (acc[date][item.action_type] || 0) + 1;
        return acc;
      }, {});

      return Object.entries(actionsByDay).map(([date, actions]) => ({
        date,
        ...actions,
        total: Object.values(actions).reduce((sum, count) => sum + count, 0)
      }));
    } catch (error) {
      console.error('Error getting actions by day:', error);
      return [];
    }
  }
};